import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph, Flex, Input,
} from '@zillow/constellation';
import {
    Col, Row, Center,
} from '@zillow/zrs-comet';


import appLogger from 'app/shared/loggers/appLogger';

import RentGuaranteeActions from 'app/shared/actions/RentGuaranteeActions';
import PostingActions from 'app/shared/actions/PostingActions';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';

import TwoWaysToPay from './TwoWaysToPay';
import RadioButton from '../components/RadioButton';

import { StyledHR } from './StyledComponents';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
    propertyId: PropTypes.string.isRequired,
    progressStepper: PropTypes.node.isRequired,
};
const defaultProps = {};

const VerifySecurityDeposit = ({
    onGoToNextStep,
    onGoToPrevStep,
    propertyId,
    progressStepper,
}) => {
    const dispatch = useDispatch();
    const {
        securityDepositAmount, monthlyRent, paymentMethod, isSecurityDepositRequired,
    } = useSelector(state => state.rentGuarantee);
    const securityDepositCurrentProperty = useSelector(state => state.properties.currentProperty.securityDeposit);
    const monthlyRentCurrentProperty = useSelector(state => state.properties.currentProperty.price);
    const [isNextDisabled, setIsNextDisabled] = useState(true);

    useEffect(() => {
        if (!securityDepositAmount) {
            if (securityDepositCurrentProperty) {
                dispatch(RentGuaranteeActions.setSecurityDepositAmount(securityDepositCurrentProperty));
            }
            if (monthlyRentCurrentProperty) {
                dispatch(RentGuaranteeActions.setMonthlyRent(monthlyRentCurrentProperty));
            } else {
                dispatch(PostingActions.property.getDetails(propertyId))
                    .then((response) => {
                        const { floorplans } = response;

                        if (floorplans.length) {
                            const { securityDeposit, price } = floorplans[0];
                            dispatch(RentGuaranteeActions.setSecurityDepositAmount(securityDeposit));
                            dispatch(RentGuaranteeActions.setMonthlyRent(price));

                            RentGuaranteeActions.setMonthlyRent(price);
                        }
                    })
                    .catch((errorRMWModel) => {
                        appLogger.logError({
                            message: errorRMWModel.message,
                            context: 'RentGuaranteePage.fetchPropertyDetails',
                        });
                    });
            }
        }
    }, []);

    useEffect(() => {
        if (!isSecurityDepositRequired && paymentMethod) {
            setIsNextDisabled(false);
        } else if (isSecurityDepositRequired && securityDepositAmount !== null) {
            setIsNextDisabled(false);
        } else {
            setIsNextDisabled(true);
        }
    }, [isSecurityDepositRequired, paymentMethod, securityDepositAmount]);

    const handleWaiveSecurityDepositClick = () => {
        dispatch(RentGuaranteeActions.setIsSecurityDepositRequired(false));

        setTimeout(() => {
            const element = document.getElementById('two-ways-to-pay');
            if (element != null && 'scrollIntoView' in element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    const handleRequireSecurityDepositClick = () => {
        dispatch(RentGuaranteeActions.setIsSecurityDepositRequired(true));

        setTimeout(() => {
            const element = document.getElementById('security-deposit');
            if (element != null && 'scrollIntoView' in element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    const onSecurityDepositChange = (event) => {
        const value = Number(event.target.value);
        dispatch(RentGuaranteeActions.setSecurityDepositAmount(value));
    };

    const renderSubtitle = () => (
        <>
            <Paragraph fontType="bodyHeading">
                Rent Guarantee protects your investment, so you can:
            </Paragraph>
            <Paragraph>- Skip the hassle of receiving, holding, and refunding a deposit.</Paragraph>
            <Paragraph marginBottom="lg">
                - Offer an attractive, modern benefit that helps your property stand out to renters who value
                 financial flexibility.
            </Paragraph>
            <Paragraph>
                If you choose to require a security deposit, this amount will be deducted from your Rent
                Guarantee coverage if you submit a claim
            </Paragraph>
        </>
    );

    return (
        <WizardBodyTemplate
            title="Require security deposit?"
            subtitle={renderSubtitle()}
            progressStepper={progressStepper}
            footer={(
                <WizardActionFooter
                    onBackClick={onGoToPrevStep}
                    onNextClick={onGoToNextStep}
                    isNextDisabled={isNextDisabled}
                />
            )}
        >
            <Row marginTop="xl" marginBottom="md">
                <Col xs={6}>
                    <RadioButton
                        onClick={handleWaiveSecurityDepositClick}
                        name="security-deposit-choice"
                        id="waive-security-deposit"
                        label="Waive security deposit"
                        checked={!isSecurityDepositRequired && isSecurityDepositRequired !== null}
                    />
                </Col>
                <Col xs={6}>
                    <RadioButton
                        onClick={handleRequireSecurityDepositClick}
                        name="security-deposit-choice"
                        id="require-security-deposit"
                        label="Require security deposit"
                        checked={isSecurityDepositRequired}
                    />
                </Col>
            </Row>
            {isSecurityDepositRequired && (
                <div id="security-deposit">
                    <Flex display="flex" alignItems="center" flexDirection="column">
                        <StyledHR color="yellow300" />
                        <Center>
                            <Paragraph fontType="h4">How much security deposit?</Paragraph>
                            <Paragraph marginBottom="lg">
                                Requiring a lower security deposit could make your listing more attractive to renters.
                                Since Rent Guarantee provides you with up to $5,000 coverage, you may not need to
                                collect the full deposit you’d require without coverage.
                            </Paragraph>
                            <Paragraph fontType="bodyHeading">
                                Consider lowering your security deposit?
                            </Paragraph>
                        </Center>
                    </Flex>
                    <FormField
                        label={<Label>Security deposit</Label>}
                        control={(
                            <AdornedInput
                                leftAdornment={<Adornment>$</Adornment>}
                                input={(
                                    <Input
                                        aria-label="Security deposit"
                                        value={securityDepositAmount}
                                        onChange={onSecurityDepositChange}
                                    />
                                )}
                            />
                        )}
                        description={<FormHelp>Amount should generally not exceed 150% of the base rent</FormHelp>}
                    />
                </div>
            )}
            {!isSecurityDepositRequired && isSecurityDepositRequired !== null && (
                <TwoWaysToPay
                    monthlyRent={monthlyRent}
                    paymentMethod={paymentMethod}
                />
            )}
        </WizardBodyTemplate>
    );
};

VerifySecurityDeposit.propTypes = propTypes;
VerifySecurityDeposit.defaultProps = defaultProps;

export default VerifySecurityDeposit;
